import type { ContentRegistry } from '@/core/content/registry';
import type { AchievementDef } from '@/core/content/schemas/progressionDefs';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import { evaluateCondition } from './conditions';
import type { HandlerEnv } from './placement/handlers';
import { grantRewards } from './rewards';

/** Progress of one achievement from durable stats (content-schemas §14.7) — backfills for free. */
export function achievementProgress(
  content: ContentRegistry,
  s: GameSave,
  def: AchievementDef,
): { current: number; target: number } {
  const p = def.progress;
  if (p.kind === 'counter') return { current: s.stats.counters[p.stat] ?? 0, target: p.target };
  if (p.kind === 'distinct') return { current: s.stats.distinct[p.stat]?.length ?? 0, target: p.target };
  return { current: evaluateCondition(content, s, p.condition).ok ? 1 : 0, target: 1 };
}

/** Pipeline step d: mark newly completed achievements. Claiming is always the player's choice. */
export function achievementSystem(
  draft: GameSave,
  _events: readonly DomainEvent[],
  env: HandlerEnv,
): DomainEvent[] {
  const out: DomainEvent[] = [];
  for (const def of env.content.achievements) {
    const st = (draft.achievements[def.id] ??= {
      completedAtPlayMs: null,
      claimedAtPlayMs: null,
      seen: false,
    });
    if (st.completedAtPlayMs !== null) continue;
    const { current, target } = achievementProgress(env.content, draft, def);
    if (current >= target) {
      st.completedAtPlayMs = draft.clock.playTimeMs;
      out.push({ type: 'ACHIEVEMENT_COMPLETED', achievementId: def.id });
    }
  }
  return out;
}

export function claimAchievement(
  draft: GameSave,
  content: ContentRegistry,
  achievementId: AchievementDef['id'],
):
  | { ok: true; events: DomainEvent[] }
  | { ok: false; error: 'NOT_FOUND' | 'NOT_COMPLETED' | 'ALREADY_CLAIMED' } {
  const def = content.achievements.find((a) => a.id === achievementId);
  const st = draft.achievements[achievementId];
  if (!def || !st) return { ok: false, error: 'NOT_FOUND' };
  if (st.completedAtPlayMs === null) return { ok: false, error: 'NOT_COMPLETED' };
  if (st.claimedAtPlayMs !== null) return { ok: false, error: 'ALREADY_CLAIMED' };
  st.claimedAtPlayMs = draft.clock.playTimeMs;
  st.seen = true;
  const events = grantRewards(draft, content, `ach:${achievementId}`, def.rewards);
  return {
    ok: true,
    events: [{ type: 'ACHIEVEMENT_CLAIMED', achievementId, rewards: [...def.rewards] }, ...events],
  };
}
